import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';

import { workspaceApi } from '@/api/workspace/index.ts';
import { mapToCashTile } from '@/domain/FxCash/FxCash.utils.ts';
import { mapToFlexiForward } from '@/domain/FxFlexiForward/FxFlexiForward.utils';
import { mapToForwardAccumulator } from '@/domain/FxForwardAccumulator/FxForwardAccumulator.utils.ts';
import { mapToOption } from '@/domain/FxOption/FxOption.utils.ts';
import { mapToOrderTile } from '@/domain/FxOrder/FxOrder.utils.ts';
import { mapToFxSwap } from '@/domain/FxSwap/FxSwap.utils.ts';
import { mapToTargetAccumulator } from '@/domain/FxTargetAccumulator/FxTargetAccumulator.utils.ts';
import type { AppState } from '@/store/store.ts';
import { tabsAdapter } from '@/store/tabs/adapter.ts';
import type { TabsState } from '@/store/tabs/types.ts';
import { tilesAdapter } from '@/store/tiles/adapter.ts';

export function reduceTabsOnfetchSharedWorkspace(builder: ActionReducerMapBuilder<TabsState>) {
  builder.addMatcher(workspaceApi.endpoints.getSharedTab.matchFulfilled, (state, { payload }) => {
    if (payload?.tabType !== 'tiles') {
      // cannot share bulk
      return;
    }

    tabsAdapter.upsertOne(state.tabs, {
      clientId: payload.clientId,
      id: payload.tabId,
      tabName: payload.tabName,
      tabType: payload.tabType,
      isShared: true,
      sharedWithUsers: payload.sharedWithEmails,
      ownerEmail: payload.ownerEmail,
    });
  });
}

export function reduceTileOnFetchSharedWorkspace(builder: ActionReducerMapBuilder<AppState['tiles']>) {
  builder.addMatcher(workspaceApi.endpoints.getSharedTab.matchFulfilled, (state, { payload }) => {
    if (!payload) {
      return;
    }

    const incomingTilesIds = new Set(payload.tiles.map((tile) => tile.tileId));
    const tilesIdsToRemove = Object.values(state.entities).reduce<string[]>((acc, tile) => {
      if (tile.tabId === payload.tabId && !incomingTilesIds.has(tile.tileId)) {
        acc.push(tile.tileId);
      }
      return acc;
    }, []);

    tilesAdapter.upsertMany(state, payload.tiles);
    tilesAdapter.removeMany(state, tilesIdsToRemove);
  });
}

export function reduceCashOnFetchSharedWorkspace(builder: ActionReducerMapBuilder<AppState['cash']>) {
  builder.addMatcher(workspaceApi.endpoints.getSharedTab.matchFulfilled, (state, { payload }) => {
    if (!payload) {
      return;
    }

    (payload.products.cash ?? []).forEach((newCash) => {
      state[newCash.tileId] = mapToCashTile(newCash);
    });
  });
}

export function reduceSwapOnFetchSharedWorkspace(builder: ActionReducerMapBuilder<AppState['swap']>) {
  builder.addMatcher(workspaceApi.endpoints.getSharedTab.matchFulfilled, (state, { payload }) => {
    if (!payload) {
      return;
    }

    (payload.products.swap ?? []).forEach((newSwap) => {
      state[newSwap.tileId] = mapToFxSwap(newSwap);
    });
  });
}

export function reduceOptionOnFetchSharedWorkspace(builder: ActionReducerMapBuilder<AppState['option']>) {
  builder.addMatcher(workspaceApi.endpoints.getSharedTab.matchFulfilled, (state, { payload }) => {
    if (!payload) {
      return;
    }

    (payload.products.option ?? []).forEach((newOption) => {
      state[newOption.tileId] = mapToOption(newOption);
    });
  });
}

export function reduceOrderOnFetchSharedWorkspace(builder: ActionReducerMapBuilder<AppState['orders']>) {
  builder.addMatcher(workspaceApi.endpoints.getSharedTab.matchFulfilled, (state, { payload }) => {
    if (!payload) {
      return;
    }

    (payload.products.order ?? []).forEach((newOrder) => {
      state[newOrder.tileId] = mapToOrderTile(newOrder);
    });
  });
}

export function reduceFlexiForwardOnFetchSharedWorkspace(builder: ActionReducerMapBuilder<AppState['flexiForward']>) {
  builder.addMatcher(workspaceApi.endpoints.getSharedTab.matchFulfilled, (state, { payload }) => {
    if (!payload) {
      return;
    }

    (payload.products.flexiForward ?? []).forEach((newFlexiForward) => {
      state[newFlexiForward.tileId] = mapToFlexiForward(newFlexiForward);
    });
  });
}

export function reduceTargetAccumulatorOnFetchSharedWorkspace(
  builder: ActionReducerMapBuilder<AppState['targetAccumulator']>,
) {
  builder.addMatcher(workspaceApi.endpoints.getSharedTab.matchFulfilled, (state, { payload }) => {
    if (!payload) {
      return;
    }

    (payload.products.targetAccumulator ?? []).forEach((newTargetAccumulator) => {
      state[newTargetAccumulator.tileId] = mapToTargetAccumulator(newTargetAccumulator);
    });
  });
}

export function reduceForwardAccumulatorOnFetchSharedWorkspace(
  builder: ActionReducerMapBuilder<AppState['forwardAccumulator']>,
) {
  builder.addMatcher(workspaceApi.endpoints.getSharedTab.matchFulfilled, (state, { payload }) => {
    if (!payload) {
      return;
    }

    (payload.products.forwardAccumulator ?? []).forEach((newForwardAccumulator) => {
      state[newForwardAccumulator.tileId] = mapToForwardAccumulator(newForwardAccumulator);
    });
  });
}
